import { useEffect, useState } from "react"
import { IAssessmentRequest } from "../interfaces/IAssessmentRequest"
import { IErrorMap } from "../interfaces/IErrorMap"
import { IAssessmentFormProps } from "../interfaces/props/IAssessmentFormProps"
import { SelectOption } from "../interfaces/SelectOption"
import { CreateAssessment } from "../services/createAssessmentService"
import Dropdown from "./Dropdown"
import Policies from "./policies"
import SubmitButton from "./SubmitButton"
import TextInput from "./TextInput"
import {IFeatureConfigurations} from "../interfaces/props/IFeatureConfigurations";
import IVisitSession from "../interfaces/IVisitSession"
import { IDropboxDisplay, defaultDropboxDisplay } from "../interfaces/IDropboxDisplay"

const AssessmentForm = ({searchParams: sp, config, t, 
  accessCodeSettings: [accessCode, setAccessCode, accessCodeErrorTextKey], 
  yobSettings: [yob, setYob, yobErrorTextKey], 
  genderSettings: [gender, setGender, genderErrorTextKey, genderSelectOptions],
  studySettings: [study, setStudy, studyErrorTextKey], 
  sessionSettings: [visitSession, setVisitSession, visitSessionErrorTextKey],
  localeSettings: [locale,setLocale,localeErrorTextKey],
  assessmentFormErrorService: {errorMap, validate, validateParams, validateAsParam},
  paramHasMatch,
  redirectSuccess,
  redirectError,
  cacheUserInfo
}: IAssessmentFormProps) => {

  const [visitSessionPlaceholderTextKey, setVisitSessionPlaceholderTextKey] = useState<string>("");
  const [visitSessionOptions, setVisitSessionOptions] = useState<Array<SelectOption>>([]);
  const [localePlaceholderTextKey, setLocalePlaceholderTextKey] = useState<string>("");
  const [localeOptions, setLocaleOptions] = useState<Array<SelectOption>>([]);
  const [isWaitingForServer, setIsWaitingForServer] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [visitSessionsForStudy, setVisitSessionsForStudy] = useState<Array<IVisitSession>>([]);
  const [dropboxDisplay, setDropboxDisplay] = useState<IDropboxDisplay>(defaultDropboxDisplay);

  const {studies, refuseInvalidFieldParamLink} = config;

  const studyOptions: SelectOption[] = studies ? studies.map(
    studyInfo => ({name: studyInfo.name, value: studyInfo.protocolName})
  ) : [{name: "", value: ""}]

  const accessCodeKey = "accessCode"
  const localeCodeKey = "localeCode"

  const UsingDefault : IFeatureConfigurations = config.featureConfigurations ||
      {useDefaultGender: false, defaultGender: "", useDefaultYOB: false, defaultYOB: ""}

  // Gender dropdown
  const genderSelectName = "select gender" ;

  const storeAccessCodeAndLocaleLocally=(accessCode:string,localeCode:string)=>{
    localStorage[accessCodeKey] = accessCode
    localStorage[localeCodeKey] = localeCode
  }

  const getLocalAccessCode = ()=>{
    return localStorage[accessCodeKey]
  }

  const getLocalLocaleCode=()=>{
    return localStorage[localeCodeKey]
  }
  const areAccessCodeAndLocaleStoredLocally=()=>{
    return (localStorage.getItem(localeCodeKey)!==null) && (localStorage.getItem(accessCodeKey)!=null)
  }


  // hook to handle auto-select of visit session based on selected study
  useEffect(()=> {
    if(!(paramHasMatch.session)) {
      const selectedStudy = studies?.find(element => element.protocolName === study)
      if (selectedStudy && selectedStudy.visitSessions) {
        const visitSessionsForStudy = selectedStudy.visitSessions
        setVisitSessionsForStudy(visitSessionsForStudy)
      	setVisitSessionPlaceholderTextKey("LABEL_SELECT_VISIT")
      	if (visitSessionsForStudy.length > 1) {
          // multiple sessions returned, clear current selection
          setVisitSession("")
      	} else {
          // if there is one valid visit session, that session is automatically selected.
          setVisitSession(visitSessionsForStudy[0].sessionCode)
      	}
      	setVisitSessionOptions(visitSessionsForStudy.map((session)=>{return {name: session.name, value: session.sessionCode}}))
      }
      else {
        setVisitSessionPlaceholderTextKey("LABEL_SELECT_STUDY_FIRST")
        setVisitSessionOptions([])
        setVisitSession("")
      }
    }
  }, [study, studies, paramHasMatch, setVisitSession, setVisitSessionsForStudy])

  useEffect(()=>{
    const selectedStudy = studies?.find(element => element.protocolName === study)
    if(paramHasMatch.locale){
        return 
    }

    if (selectedStudy && selectedStudy.studyLocalities) {
      const localitiesForStudy = selectedStudy.studyLocalities
      setLocalePlaceholderTextKey("Select Locale")
       
      if(Object.entries(localitiesForStudy).length > 1){
        setLocale("")
      }else{
        setLocale(Object.keys(localitiesForStudy)[0])
   } 

      setLocaleOptions(Object.entries(localitiesForStudy).map(([k,v]) => {return {name:v,value:k}}))
    }
    else {
      setLocalePlaceholderTextKey("LABEL_SELECT_STUDY_FIRST")
      setLocaleOptions([])
      setLocale("")
    }
  },[study,studies])

  useEffect(() => {
    const {studies} = config
    if (studies === null) return
    dropboxDisplay.study = studies.length > 1
    
    for(let studyIdx = 0; studyIdx < studies.length && (!dropboxDisplay.visit || !dropboxDisplay.locale); studyIdx++) {
      const study = studies[studyIdx];

      if (!dropboxDisplay.visit) {
        dropboxDisplay.visit = study.visitSessions.length > 1
      }

      if(!dropboxDisplay.locale) {
        dropboxDisplay.locale = Object.keys(study.studyLocalities).length > 1
      }
    }

    setDropboxDisplay(dropboxDisplay)

  }, [])

  const submitForm = () => {
    let validForm = validate() 
    let validParams = true;

    if (refuseInvalidFieldParamLink && !sp.paramsEmpty) {
      validParams = validateParams();
    }

    const visitSessions = studies?.find(element => element.protocolName === study)?.visitSessions
    const fullVisitSession = visitSessions?.find(element => element.sessionCode === visitSession)

    if (((validForm && validParams) || areAccessCodeAndLocaleStoredLocally())) {
      let mode=""
      if(fullVisitSession){
        mode = fullVisitSession.mode?fullVisitSession.mode:""
      }
      let data: IAssessmentRequest = {
        externalId: accessCode,
        yob: UsingDefault.useDefaultYOB === true && UsingDefault.defaultYOB !== "" ? UsingDefault.defaultYOB : yob,
        genderCode: gender,
        visitSessionCode: visitSession,
        localityCode: locale,
        redirectSuccess: redirectSuccess,
        redirectError: redirectError,
        mode: mode === "smartphone" ? mode : ""
      }

      if(areAccessCodeAndLocaleStoredLocally()){
        data={
          externalId:getLocalAccessCode(),
          localityCode:getLocalLocaleCode(),
          mode: mode === "smartphone" ? mode : ""
        }
      }
      
      setIsWaitingForServer(true)
      
      if(cacheUserInfo){
        storeAccessCodeAndLocaleLocally(accessCode,locale)
      }

      CreateAssessment(data,study===""?null:study).then((success)=>{
        if(!success) {
          setIsWaitingForServer(false)
        }
      })
    }
  }

  const showField = (errorMap: IErrorMap, param?: string, paramHasMatch?: boolean): boolean => {
    let hide = param && validateAsParam(errorMap, paramHasMatch)
    
    if (paramHasMatch){
      hide = hide && paramHasMatch
    }

    return !(hide); 
  }

  const startTestDisabled = isWaitingForServer || !termsAccepted;

  return (
    <>
      <div className='menu'>
        {
         showField(errorMap.gender, sp.gender, paramHasMatch.gender) && !areAccessCodeAndLocaleStoredLocally() &&
          <Dropdown 
            change = {(newVal:string) => {setGender(newVal)}}
            options = {genderSelectOptions}
            selectName = {genderSelectName}
            placeholderTextKey = "LABEL_SELECT_GENDER"
            errorTextKey = {genderErrorTextKey}
            value = {gender}
            autoSelectSingleOption = {false}
            hideFeatureFlag = {UsingDefault.useDefaultGender}
            defaultValue = {UsingDefault.defaultGender}
          />
        }
        {
          showField(errorMap.study, sp.study, paramHasMatch.study) && !areAccessCodeAndLocaleStoredLocally() &&
          <Dropdown 
            change = {(newVal:string) => {setStudy(newVal)}}
            options = {studyOptions}
            selectName = "study select"
            placeholderTextKey = "LABEL_SELECT_STUDY"
            errorTextKey = {studyErrorTextKey}
            value = {study}
            autoSelectSingleOption = {true}
            display = {dropboxDisplay.study}
          />
        }
        {
          showField(errorMap.accessCode, sp.accessCode) && !areAccessCodeAndLocaleStoredLocally() &&
          <TextInput 
            change = {(newVal:string) => {setAccessCode(newVal)}} 
            placeholderTextKey = "LABEL_ACCESS_CODE_PLEASE_ENTER"
            errorTextKey = {accessCodeErrorTextKey}
            value = {accessCode}
          />
        }
        {
          showField(errorMap.yob, sp.yob, paramHasMatch.yob) && !areAccessCodeAndLocaleStoredLocally() &&
          <TextInput 
            change = {(newVal:string) => {setYob(newVal); paramHasMatch.yob  =  false}}
            placeholderTextKey = "LABEL_YEAR_OF_BIRTH_PLEASE_ENTER"
            errorTextKey = {yobErrorTextKey}
            value = {yob}
            hideFeatureFlag = {UsingDefault.useDefaultYOB}
            defaultValue = {UsingDefault.defaultYOB}
          />
        }
        {
          showField(errorMap.session, sp.session, paramHasMatch.session) && !areAccessCodeAndLocaleStoredLocally() &&
          <Dropdown
            change = {(newVal:string) => {setVisitSession(newVal)}}
            options = {visitSessionOptions}
            selectName = "study select"
            placeholderTextKey = {visitSessionPlaceholderTextKey}
            errorTextKey = {visitSessionErrorTextKey}
            value = {visitSession}
            autoSelectSingleOption = {true}
            display  =  {dropboxDisplay.visit}
          />
        }
        {
          showField(errorMap.locale, sp.locale, paramHasMatch.locale) && !areAccessCodeAndLocaleStoredLocally() &&
          <Dropdown
            change = {(newVal:string) => {setLocale(newVal)}}
            options = {localeOptions}
            selectName = "locale select"
            placeholderTextKey = {localePlaceholderTextKey}
            errorTextKey = {localeErrorTextKey}
            value = {locale}
            autoSelectSingleOption = {true}
            defaultOptionValue = {config.localityCode}
            display = {dropboxDisplay.locale}
          />
        }
        <Policies termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted} config={config} t={t} />
        <SubmitButton 
          className = {startTestDisabled ? "submit-button--disabled":"submit-button"} 
          action = {submitForm} 
          buttonTextKey = "BUTTON_START_THE_TEST_UPPER_CASE" 
          disabled  = {startTestDisabled}
        />
      </div>
    </>
  )
}

export default AssessmentForm